import * as Accordion from "@radix-ui/react-accordion";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { reverse } from "@reactivated";

import { ChatLink } from "../../../components/ChatLink";
import { SVGIcon } from "../../../components/SVGIcon";
import { isoWebPageURL } from "../../../models/nominals";
import { format } from "../../../utils/format";
import { usePageURL } from "../../../utils/urls";
import { TrackShipmentDetailsCustomerInfo } from "../TrackShipmentDetailsCustomerInfo";
import { UnifiedOrderLookupOrder } from "../types";

import deliveryStyles from "./delivery-styles.module.scss";
import styles from "./index.module.scss";

const AccordionIcon = () => (
    <>
        <SVGIcon
            aria-hidden="true"
            data-name="closed"
            name="carretDownAccount"
        />
        <SVGIcon aria-hidden="true" data-name="open" name="carretDownAccount" />
    </>
);

export const TrackOrderSummaryHeader = (
    props: Immutable<{
        order: UnifiedOrderLookupOrder;
    }>,
): React.JSX.Element => {
    const contactURL = usePageURL("contact_link");
    const homeURL = usePageURL("home");
    const chatOfflineURL = contactURL || homeURL;
    const customerLoginURL = isoWebPageURL.wrap(reverse("customer:login"));
    const customerRegisterURL = isoWebPageURL.wrap(
        reverse("customer:register"),
    );
    return (
        <header className={styles.header}>
            <h3 className={styles.title}>
                <strong>Order Number: </strong>
                {props.order.searched_order_number || props.order.order_number}
            </h3>
            <p className={styles.supportText}>
                {t`Need Help?`}{" "}
                <ChatLink
                    className={styles.supportButton}
                    chatOnlineText={t`Chat Now`}
                    chatOfflineLink={chatOfflineURL}
                />
            </p>
            <Accordion.Root
                className={styles.accordionWrapper}
                type="single"
                collapsible
            >
                <Accordion.Item
                    className={styles.accordionItem}
                    value="order-details"
                >
                    <Accordion.Trigger asChild>
                        <button className={styles.detailsToggle}>
                            {t`Order Details`} <AccordionIcon />
                        </button>
                    </Accordion.Trigger>
                    <Accordion.Content className={styles.accordionContent}>
                        <TrackShipmentDetailsCustomerInfo
                            shippingAddress={props.order.shipping_address}
                            customerLoginURL={customerLoginURL}
                            customerRegisterURL={customerRegisterURL}
                            customStyles={deliveryStyles}
                        />
                        {props.order.is_tax_known && (
                            <div>
                                <h4>{t`Order Summary`}</h4>
                                <dl>
                                    <div className={styles.orderSummaryRow}>
                                        <dt>{t`Subtotal:`}</dt>
                                        <dd>
                                            {format.money(
                                                props.order
                                                    .basket_total_before_discounts_excl_tax,
                                            )}
                                        </dd>
                                    </div>

                                    <div className={styles.orderSummaryRow}>
                                        <dt>{t`Shipping:`}</dt>
                                        <dd>
                                            {format.money(
                                                props.order.shipping_excl_tax,
                                            )}
                                        </dd>
                                    </div>

                                    {props.order.is_tax_known ? (
                                        <>
                                            {props.order.itemized_tax_groups.map(
                                                (group, index) => (
                                                    <div
                                                        key={index}
                                                        className={
                                                            styles.orderSummaryRow
                                                        }
                                                    >
                                                        <dt>{group.name}</dt>
                                                        <dd>
                                                            {format.money(
                                                                group.amount,
                                                            )}
                                                        </dd>
                                                    </div>
                                                ),
                                            )}
                                        </>
                                    ) : (
                                        <div className={styles.orderSummaryRow}>
                                            <dt>{t`Tax:`}</dt>
                                            <dd>{t`Tax will be calculated upon shipment`}</dd>
                                        </div>
                                    )}

                                    {props.order.total_discount_excl_tax && (
                                        <div className={styles.orderSummaryRow}>
                                            <dt>{t`Discount:`}</dt>
                                            <dd>
                                                -
                                                {format.money(
                                                    props.order
                                                        .total_discount_excl_tax,
                                                )}
                                            </dd>
                                        </div>
                                    )}

                                    <div className={styles.orderSummaryRow}>
                                        <dt>
                                            <strong>{t`Order Total:`}</strong>
                                        </dt>
                                        <dd>
                                            <strong>
                                                {format.money(
                                                    props.order.total_incl_tax,
                                                )}
                                            </strong>
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        )}
                    </Accordion.Content>
                </Accordion.Item>
            </Accordion.Root>
        </header>
    );
};
